// The header's main navigation: five top-level links, the rest in a "More" menu (Header.tsx).

import type { Role } from '../api.ts';
import type { Route } from '../router.ts';
import { href } from '../router.ts';
import type { IconName } from './Icon.tsx';

export type Section = 'console' | 'wallboard' | 'events' | 'alarms' | 'master' | 'imports' | 'erpSync' | 'ingest' | 'setup' | 'reports' | 'audit';

export function sectionOf(r: Route): Section | null {
  switch (r.name) {
    case 'qa':
    case 'aiReport':
      return 'wallboard';
    case 'console':
    case 'wallboard':
    case 'events':
    case 'setup':
    case 'reports':
    case 'audit':
      return r.name;
    case 'alarms':
      return 'alarms';
    case 'companies':
    case 'sites':
    case 'site':
      return 'master';
    case 'imports':
    case 'import':
      return 'imports';
    case 'ingest':
      return 'ingest';
    case 'erpSync':
      return 'erpSync';
    default:
      return null;
  }
}

export type NavGroup = 'Data' | 'Oversight';

export interface NavItem {
  key: Section;
  label: string;
  icon: IconName;
  to: string;
  roles?: Role[];
  /** Set on the items that live in the More menu, under this heading. */
  group?: NavGroup;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { key: 'console', label: 'Console', icon: 'console', to: href.console(), roles: ['operator', 'supervisor', 'admin'] },
  { key: 'wallboard', label: 'Supervisor', icon: 'users', to: href.wallboard(), roles: ['supervisor', 'admin'] },
  { key: 'events', label: 'Events', icon: 'list', to: href.events(), roles: ['supervisor', 'admin'] },
  { key: 'setup', label: 'Setup', icon: 'gear', to: href.setup(), roles: ['supervisor', 'admin'] },
  { key: 'reports', label: 'Reports', icon: 'download', to: href.reports(), roles: ['supervisor', 'admin'] },
  { key: 'master', label: 'Master Data', icon: 'db', to: href.sites(), group: 'Data' },
  { key: 'imports', label: 'Imports', icon: 'upload', to: href.imports(), group: 'Data' },
  { key: 'erpSync', label: 'ERP Sync', icon: 'sync', to: href.erpSync(), roles: ['supervisor', 'admin'], group: 'Data' },
  { key: 'ingest', label: 'Ingest', icon: 'mail', to: href.ingest(), roles: ['admin'], group: 'Data' },
  { key: 'alarms', label: 'Alarms', icon: 'bolt', to: href.alarms(), group: 'Oversight' },
  { key: 'audit', label: 'Audit', icon: 'shield', to: href.audit(), roles: ['supervisor', 'admin', 'auditor'], group: 'Oversight' },
];

export interface NavLayout {
  /** Plain links, in bar order. */
  links: NavItem[];
  /** The More menu's sections; empty when there is no menu. */
  more: { group: NavGroup; items: NavItem[] }[];
  /** The More item for the current page, which the trigger names. */
  moreCurrent: NavItem | null;
}

/**
 * What the user `can` see, laid out. A lone overflow item is a plain link rather than a one-item menu, and so is every
 * overflow item when the user has no top-level link (an auditor sees only overflow items, and few of them).
 */
export function navLayout(can: (roles: Role[]) => boolean, cur: Section | null, all: readonly NavItem[] = NAV_ITEMS): NavLayout {
  const visible = all.filter((i) => !i.roles || can(i.roles));
  const top = visible.filter((i) => !i.group);
  const overflow = visible.filter((i) => i.group);
  if (overflow.length <= 1 || top.length === 0) return { links: visible, more: [], moreCurrent: null };
  const more: NavLayout['more'] = [];
  for (const i of overflow) {
    const sec = more.find((m) => m.group === i.group);
    if (sec) sec.items.push(i);
    else more.push({ group: i.group!, items: [i] });
  }
  return { links: top, more, moreCurrent: overflow.find((i) => i.key === cur) ?? null };
}
